import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getUrl, remove } from "aws-amplify/storage";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { listArts } from "../../graphql/queries";
import { deleteArt } from "../../graphql/mutations";
import { useUserContext } from "../../UserContext";
import { isRecordOwner } from "../../utils/ownerMatch";
import "./ArtGallery.css";

export interface ArtItem {
  id: string;
  title: string;
  artist: string;
  imageKey: string;
  createdAt: string;
  imageUrl?: string;
  owner?: string | null;
}

const client = generateClient();

const ArtGallery: React.FC = () => {
  const { user } = useUserContext();
  const [artItems, setArtItems] = useState<ArtItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArt, setSelectedArt] = useState<ArtItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [authIdentities, setAuthIdentities] = useState<string[]>([]);

  useEffect(() => {
    const loadAuthIdentities = async () => {
      if (!user) {
        setAuthIdentities([]);
        return;
      }
      try {
        const { userId, username } = await getCurrentUser();
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;
        const sub = payload?.sub as string | undefined;
        const cognitoUsername = payload?.["cognito:username"] as
          | string
          | undefined;

        setAuthIdentities(
          [
            userId,
            username,
            sub,
            cognitoUsername,
            user.id,
            user.username,
            user.owner,
          ].filter((id): id is string => Boolean(id))
        );
      } catch {
        setAuthIdentities(
          [user.id, user.username, user.owner].filter((id): id is string =>
            Boolean(id)
          )
        );
      }
    };

    loadAuthIdentities();
  }, [user]);

  const canDelete = (art: ArtItem) => {
    if (authIdentities.length === 0) return false;
    return isRecordOwner(art.owner, authIdentities);
  };

  useEffect(() => {
    const fetchArt = async () => {
      try {
        const response = await client.graphql({ query: listArts });
        const rawItems =
          (response as { data?: { listArts?: { items: ArtItem[] } } })?.data
            ?.listArts?.items ?? [];

        const itemsWithUrls = await Promise.all(
          rawItems.map(async (item) => {
            const { url } = await getUrl({
              key: item.imageKey,
              options: { accessLevel: "guest" },
            });
            return { ...item, imageUrl: url.href };
          })
        );

        itemsWithUrls.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setArtItems(itemsWithUrls);
      } catch (error) {
        console.error("Error fetching art:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArt();
  }, []);

  const openModal = (art: ArtItem) => {
    setConfirmDeleteId(null);
    setSelectedArt(art);
  };

  const closeModal = () => {
    setSelectedArt(null);
    setConfirmDeleteId(null);
  };

  const handleDelete = async (art: ArtItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!canDelete(art) || deletingId || confirmDeleteId !== art.id) return;

    setDeletingId(art.id);
    try {
      if (art.imageKey) {
        await remove({ key: art.imageKey });
      }
      await client.graphql({
        query: deleteArt,
        variables: { input: { id: art.id } },
      });
      setArtItems((prev) => prev.filter((item) => item.id !== art.id));
      if (selectedArt?.id === art.id) {
        closeModal();
      }
      setConfirmDeleteId(null);
    } catch (error) {
      console.error("Error deleting art:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setConfirmDeleteId(null);
  };

  return (
    <div className="art-gallery-container">
      <div className="art-gallery-panel">
        <div className="art-gallery-subnav">
          <h1>Art Gallery</h1>
          <Link to="/art-gallery/upload" className="primary-button">
            Upload Art
          </Link>
        </div>

        <div className="art-gallery-grid">
          {loading ? (
            <div className="art-gallery-message">Loading artwork...</div>
          ) : artItems.length === 0 ? (
            <div className="art-gallery-message">No artwork yet!</div>
          ) : (
            artItems.map((art, index) => (
              <button
                key={art.id}
                type="button"
                className="art-card polaroid"
                style={{ "--polaroid-tilt": `${((index % 6) - 2.5) * 1.4}deg` } as React.CSSProperties}
                onClick={() => openModal(art)}
              >
                <div className="polaroid-frame">
                  <div className="polaroid-image-wrap">
                    {canDelete(art) && (
                      <button
                        type="button"
                        className="polaroid-delete-btn"
                        aria-label={`Remove ${art.title}`}
                        title="Remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(art.id);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    )}
                    {confirmDeleteId === art.id && canDelete(art) && (
                      <div
                        className="polaroid-delete-overlay"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p>Remove this piece?</p>
                        <div className="polaroid-delete-confirm-row">
                          <button
                            type="button"
                            className="polaroid-delete-confirm"
                            onClick={(e) => handleDelete(art, e)}
                            disabled={deletingId === art.id}
                          >
                            {deletingId === art.id ? "…" : "Remove"}
                          </button>
                          <button
                            type="button"
                            className="polaroid-delete-cancel"
                            onClick={cancelDelete}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {art.imageUrl ? (
                      <img
                        src={art.imageUrl}
                        alt={art.title}
                        className="polaroid-image"
                      />
                    ) : (
                      <div className="polaroid-placeholder">No image</div>
                    )}
                  </div>
                  <div className="polaroid-caption">
                    <span className="polaroid-title">{art.title}</span>
                    <span className="polaroid-artist">{art.artist}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <Dialog
        open={Boolean(selectedArt)}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        className="art-modal"
      >
        {selectedArt && (
          <div className="art-modal-content">
            <IconButton
              className="art-modal-close"
              aria-label="Close"
              onClick={closeModal}
            >
              <CloseIcon />
            </IconButton>
            <div className="polaroid-frame polaroid-frame--modal">
              <div className="polaroid-image-wrap">
                {selectedArt.imageUrl && (
                  <img
                    src={selectedArt.imageUrl}
                    alt={selectedArt.title}
                    className="polaroid-image"
                  />
                )}
              </div>
              <div className="polaroid-caption">
                <span className="polaroid-title">{selectedArt.title}</span>
                <span className="polaroid-artist">{selectedArt.artist}</span>
                {canDelete(selectedArt) && (
                  <div className="polaroid-delete-actions">
                    {confirmDeleteId === selectedArt.id ? (
                      <>
                        <span className="polaroid-delete-prompt">
                          Remove this piece?
                        </span>
                        <div className="polaroid-delete-confirm-row">
                          <button
                            type="button"
                            className="polaroid-delete-confirm"
                            onClick={() => handleDelete(selectedArt)}
                            disabled={deletingId === selectedArt.id}
                          >
                            {deletingId === selectedArt.id
                              ? "Removing…"
                              : "Yes, remove"}
                          </button>
                          <button
                            type="button"
                            className="polaroid-delete-cancel"
                            onClick={cancelDelete}
                            disabled={deletingId === selectedArt.id}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="polaroid-delete-link"
                        onClick={() => setConfirmDeleteId(selectedArt.id)}
                      >
                        Remove from gallery
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ArtGallery;
